import { AxiosError } from 'axios';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import Notification from '../../shared/components/Notification/index';
import { NotificationTypes } from '../../enums/notificationTypes';
import { generatePath } from 'react-router-dom';
import { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { mapIconServiceMessages } from './mapicon.constants';
import { QueryKeys } from '../../enums/queryKeys';
import { MutationKeys } from '../../enums/mutationKeys';

export interface RecentIconItem {
  iconKey: string;
  url: string;
  lastUsedAt: string;
}

const MapIconsService = () => {
  const getMapIcons = (): UseQueryOptions<any[], Error> => ({
    queryKey: [QueryKeys.GET_MAP_ICONS],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.MAP_ICONS);
        return response.data;
      } catch (error) {
        const axiosError = error as AxiosError<any>;
        Notification({
          message: mapIconServiceMessages.GET_MAP_ICONS.ERROR,
          description: axiosError.response?.data?.message ?? mapIconServiceMessages.FALLBACK_ERROR,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
  });

  const fetchIconUrl = (s3Key?: string): UseQueryOptions<string, Error> => ({
    queryKey: [QueryKeys.FETCH_ICON_URL, s3Key],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(generatePath(ApiRoutes.ICON_URL, { key: s3Key }));
        return response.data.url;
      } catch (error) {
        const axiosError = error as AxiosError<any>;
        Notification({
          message: mapIconServiceMessages.FETCH_ICON_URL.ERROR,
          description: axiosError.response?.data?.message ?? mapIconServiceMessages.FALLBACK_ERROR,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: !!s3Key,
  });

  const getPresignedUrl = (): UseMutationOptions<{ url: string; key: string }, Error, { fileName: string; fileType: string }> => ({
    mutationKey: [MutationKeys.GET_ICON_PRESIGNED_URL],
    mutationFn: async ({ fileName, fileType }) => {
      const response = await axiosInstance.post(ApiRoutes.ICON_PRESIGN, {
        fileName,
        fileType,
      });

      const { url, key } = response.data;
      return { url, key };
    },
    onError: (error) => {
      const axiosError = error as AxiosError<any>;
      Notification({
        message: mapIconServiceMessages.GET_PRESIGNED_URL.ERROR,
        description: axiosError.response?.data?.message ?? mapIconServiceMessages.FALLBACK_ERROR,
        type: NotificationTypes.ERROR,
      });
    },
  });

  const uploadIcon = (): UseMutationOptions<boolean, Error, { key: string; name: string }> => ({
    mutationKey: [MutationKeys.UPLOAD_ICON],
    mutationFn: async ({ key, name }) => {
      const response = await axiosInstance.post(ApiRoutes.MAP_ICONS, {
        key,
        name,
      });

      return response.data?.success ?? true;
    },
    onSuccess: () => {
      Notification({
        message: mapIconServiceMessages.UPLOAD_ICON.SUCCESS,
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      const axiosError = error as AxiosError<any>;
      Notification({
        message: mapIconServiceMessages.UPLOAD_ICON.ERROR,
        description: axiosError.response?.data?.message ?? mapIconServiceMessages.FALLBACK_ERROR,
        type: NotificationTypes.ERROR,
      });
    },
  });

  // US-101662 Scenario 5: per-user list of 10 most-recent icons. Backed by
  // the same react-query cache as the rest of the icon flows.
  const getRecentIcons = (): UseQueryOptions<RecentIconItem[], Error> => ({
    queryKey: [QueryKeys.GET_RECENT_ICONS],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.RECENT_ICONS);
        return response.data;
      } catch (error) {
        const axiosError = error as AxiosError<any>;
        // Quiet error: a stale token or no recents shouldn't break the modal.
        // Only surface a toast on a real server error.
        if ((axiosError.response?.status ?? 0) >= 500) {
          Notification({
            message: mapIconServiceMessages.GET_RECENT_ICONS.ERROR,
            description: axiosError.response?.data?.message ?? mapIconServiceMessages.FALLBACK_ERROR,
            type: NotificationTypes.ERROR,
          });
        }
        throw error;
      }
    },
  });

  // Fire-and-forget — caller does not block on the response.
  const touchRecentIcon = (): UseMutationOptions<void, Error, { key: string }> => ({
    mutationKey: [MutationKeys.TOUCH_RECENT_ICON],
    mutationFn: async ({ key }) => {
      await axiosInstance.post(ApiRoutes.RECENT_ICONS, { key });
    },
    // No success toast — this is a background bookkeeping call.
  });

  return {
    getMapIcons,
    fetchIconUrl,
    getPresignedUrl,
    uploadIcon,
    getRecentIcons,
    touchRecentIcon,
  };
};

export default MapIconsService;
